import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import i18n from "@/i18n";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";
import { getCurrentClinicCustomerId } from "@/lib/utils/clinic-context";

export interface ServiceGroupRow {
  id: string;
  name: string;
  customer_id: string | null;
  created_at: string;
  updated_at: string;
}

type Untyped = {
  from: (t: string) => {
    select: (c: string) => {
      order: (c: string, o: object) => {
        or: (f: string) => PromiseLike<{ data: ServiceGroupRow[] | null; error: unknown }>;
      };
    };
    insert: (row: object) => {
      select: () => { single: () => PromiseLike<{ data: ServiceGroupRow | null; error: unknown }> };
    };
  };
};

// service_groups is newer than the generated types.ts (never edited by hand).
const db = supabase as unknown as Untyped;

export const useServiceGroups = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { data: customerId } = useCurrentClinicCustomerId();
  return useQuery({
    queryKey: ["service-groups", customerId],
    enabled: enabled && !!customerId,
    queryFn: async () => {
      const { data, error } = await db
        .from("service_groups")
        .select("*")
        .order("name", { ascending: true })
        .or(`customer_id.is.null,customer_id.eq.${customerId}`);
      if (error) throw error;
      return data ?? [];
    },
  });
};

export const useCreateServiceGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string }) => {
      const customerId = await getCurrentClinicCustomerId();
      const { data, error } = await db
        .from("service_groups")
        .insert({ name: payload.name.trim(), customer_id: customerId })
        .select()
        .single();
      if (error) throw error;
      return data as ServiceGroupRow;
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["commissions"] });
      // Wait for the refetch: the Radix select resets a value that has no matching option yet,
      // so the caller must only select the new group once it is in the list.
      await queryClient.invalidateQueries({ queryKey: ["service-groups"] });
      toast.success(i18n.t("erp:itemForm.groupToasts.created"));
    },
    onError: () => toast.error(i18n.t("erp:itemForm.groupToasts.createError")),
  });
};
